<template>
  <div class="w-full">
    <section v-if="hasConfig">

      <group-statuses
        v-if="advanceMethod === 'grouped'"
        :ref="'refGroupStatuses' + layerMapped.code"
        :layer-id="layerMapped.id"
        :groups="groups"
        :ungrouped-states="layerMapped.ungrouped || []"
        :code-layer="layerMapped.code"
        :weighting-criteria="weightingCriteria"
        :visibility="visibilityStatuses"
      />

      <statuses
        v-else-if="advanceMethod === 'default'"
        :ref="'refStatuses' + layerMapped.code"
        :layer-id="layerMapped.id"
        :statuses="statusItems"
        :type-status="typeStatus"
        :code-layer="layerMapped.code"
        :weighting-criteria="weightingCriteria"
        :visibility="visibilityStatuses"
      />

      <div v-else class="text-center py-4 text-gray-500 text-sm">
        <p>No hay método de avance configurado. Por favor, configure el método de avance en la configuración.</p>
      </div>
    </section>
  </div>
</template>

<script>
import Statuses from '../statuses/statuses.vue';
import GroupStatuses from '../group-statuses/group-statuses.vue';
import { mapLayer } from '../../services/layers.service.js';

import { createLogger } from "../../utils/logger";
const logger = createLogger('LayerStatusesManager');

export default {
  name: 'LayerStatusesManager',

  components: {
    Statuses,
    GroupStatuses,
  },

  props: {
    // Layer crudo del backend (con statuses, group_statuses, incident_statuses).
    // El manager hace clone profundo y lo conserva como fuente de verdad interna.
    initialLayerData: {
      type: Object,
      required: true,
    },
    // Objeto con métodos HTTP injectado por el padre. Inversión de control:
    // el manager no conoce la API, solo la consume.
    // Contrato esperado: { addStatus, updateStatus, deleteStatus, addGroup,
    // updateGroup, deleteGroup, linkStatusToGroup, addIncident, updateIncident,
    // deleteIncident } — cada método retorna Promise<{status?}>.
    api: {
      type: Object,
      required: true,
    },
    // Nuevo: config inyectada por el padre (valor persistido). Si no se pasa,
    // cae al storage del layerData (modo standalone / tests).
    config:           { type: Object, default: null },
    typeStatus: {
      type: String,
      default: 'status' // puede ser 'status' o 'incident'
    }
  },

  data() {
    return {
      // Deep clone para no mutar la prop. Esta es la fuente de verdad mutable.
      layerData: JSON.parse(JSON.stringify(this.initialLayerData)),
    };
  },

  provide() {
    return {
      // Mutadores nuevos — todos retornan Promise para que los hijos manejen UX (loading/error).
      AddStatus:         this._addStatus,
      UpdateStatus:      this._updateStatus,
      DeleteStatus:      this._deleteStatus,
      AddGroup:          this._addGroup,
      UpdateGroup:       this._updateGroup,
      DeleteGroup:       this._deleteGroup,
      LinkStatusToGroup: this._linkStatusToGroup,
      AddIncident:       this._addIncident,
      UpdateIncident:    this._updateIncident,
      DeleteIncident:    this._deleteIncident,
    };
  },

  computed: {
    effectiveConfig() {
      return this.config ?? this.layerMapped.config?.statuses ?? null;
    },
    // Fuente de verdad mapeada — recalcula automáticamente al mutar layerData
    layerMapped() {
      return mapLayer(this.layerData);
    },

    hasConfig() {
      return !!(this.effectiveConfig);
    },

    statusItems() {
      // Renombrado: era _GetStatuses(). Devuelve grupos o statuses planos
      // según typeStatus; el shape lo decide mapLayer.
      if (this.typeStatus === 'incident') {
        return this.layerMapped.incidents || [];
      }
      return this.layerMapped.statuses || [];
    },

    advanceMethod() {
      // Las incidencias SIEMPRE se muestran sin grupos (modo 'default')
      if (this.typeStatus === 'incident') {
        return 'default';
      }
      return this.effectiveConfig?.avance_method ?? null;
    },

    weightingCriteria() {
      // Las incidencias NO tienen criterio de ponderación
      if (this.typeStatus === 'incident') {
        return null;
      }
      return this.effectiveConfig?.weighting_criteria ?? null;
    },

    visibilityStatuses() {
      const visibility = this.effectiveConfig?.visibility;
      if (this.typeStatus === 'status')   return visibility?.progress  ?? 'visible_default';
      if (this.typeStatus === 'incident') return visibility?.incidents ?? 'visible_default';
      return 'visible_default';
    },

    // Shape estable: cada uno con significado fijo (sin polimorfismo)
    groups()    { return this.layerMapped.groups; },
    statuses()  { return this.layerMapped.statuses; },
    ungrouped() { return this.layerMapped.ungrouped; },
    incidents() { return this.layerMapped.incidents; },
  },

  watch: {
    // Si el padre cambia de capa o re-fetchea, resincronizamos.
    initialLayerData: {
      handler(newVal) {
        this.layerData = JSON.parse(JSON.stringify(newVal));
      },
      deep: false,
    },
  },

  methods: {
    _tempId() {
      // IDs temporales negativos — convención existente del proyecto (group-statuses
      // y statuses ya usan esto). Garantiza único entre statuses, grupos e incidents.
      const all = [
        ...(this.layerData.statuses || []),
        ...(this.layerData.group_statuses || []),
        ...(this.layerData.incident_statuses || []),
      ];
      const max = all.length ? Math.max(...all.map(s => Math.abs(s.id || 0))) : 0;
      return -(max + 1);
    },

    async _addStatus(payload) {
      const tempId = this._tempId();
      const optimistic = {
        id: tempId,
        layer_id: this.layerData.id,
        type: 'status',
        code: payload.code ?? null,
        parent_id: payload.parent_id ?? payload.group_id ?? null,
        permissions: [],
        ...payload,
      };
      this.layerData.statuses.push(optimistic);
      try {
        const res = await this.api.addStatus(payload);
        this._assertOk(res, 'add_status_failed');
        const i = this.layerData.statuses.findIndex(s => s.id === tempId);
        if (i !== -1) this.$set(this.layerData.statuses, i, res.status);
        return res.status;
      } catch (e) {
        this.layerData.statuses = this.layerData.statuses.filter(s => s.id !== tempId);
        throw e;
      }
    },

    async _updateStatus(id, payload) {
      const idx = this.layerData.statuses.findIndex(s => s.id === id);
      if (idx === -1) throw new Error('status_not_found');
      const prev = { ...this.layerData.statuses[idx] };
      this.$set(this.layerData.statuses, idx, { ...prev, ...payload });
      try {
        const res = await this.api.updateStatus(id, payload);
        this._assertOk(res, 'update_status_failed');
        this.$set(this.layerData.statuses, idx, res.status);
        return res.status;
      } catch (e) {
        this.$set(this.layerData.statuses, idx, prev);
        throw e;
      }
    },

    async _deleteStatus(id) {
      const idx = this.layerData.statuses.findIndex(s => s.id === id);
      if (idx === -1) return;
      const prev = this.layerData.statuses[idx];
      this.layerData.statuses.splice(idx, 1);
      try {
        const res = await this.api.deleteStatus(id);
        this._assertOk(res, 'delete_status_failed', false);
      } catch (e) {
        this.layerData.statuses.splice(idx, 0, prev);
        throw e;
      }
    },

    async _addGroup(payload) {
      const tempId = this._tempId();
      const optimistic = {
        id: tempId,
        layer_id: this.layerData.id,
        type: 'group',
        parent_id: null,
        ...payload,
      };
      if (!Array.isArray(this.layerData.group_statuses)) {
        this.$set(this.layerData, 'group_statuses', []);
      }
      this.layerData.group_statuses.push(optimistic);
      try {
        const res = await this.api.addGroup(payload);
        this._assertOk(res, 'add_group_failed');
        const i = this.layerData.group_statuses.findIndex(s => s.id === tempId);
        if (i !== -1) this.$set(this.layerData.group_statuses, i, res.status);
        return res.status;
      } catch (e) {
        this.layerData.group_statuses = this.layerData.group_statuses.filter(s => s.id !== tempId);
        throw e;
      }
    },

    async _updateGroup(id, payload) {
      const idx = (this.layerData.group_statuses || []).findIndex(s => s.id === id);
      if (idx === -1) throw new Error('group_not_found');
      const prev = { ...this.layerData.group_statuses[idx] };
      this.$set(this.layerData.group_statuses, idx, { ...prev, ...payload });
      try {
        const res = await this.api.updateGroup(id, payload);
        this._assertOk(res, 'update_group_failed');
        this.$set(this.layerData.group_statuses, idx, res.status);
        return res.status;
      } catch (e) {
        this.$set(this.layerData.group_statuses, idx, prev);
        throw e;
      }
    },

    async _deleteGroup(id) {
      const idx = (this.layerData.group_statuses || []).findIndex(s => s.id === id);
      if (idx === -1) return;
      const prev = this.layerData.group_statuses[idx];
      this.layerData.group_statuses.splice(idx, 1);
      try {
        const res = await this.api.deleteGroup(id);
        this._assertOk(res, 'delete_group_failed', false);
      } catch (e) {
        this.layerData.group_statuses.splice(idx, 0, prev);
        throw e;
      }
    },

    async _linkStatusToGroup(statusId, groupId) {
      const idx = this.layerData.statuses.findIndex(s => s.id === statusId);
      if (idx === -1) throw new Error('status_not_found');
      const prev = { ...this.layerData.statuses[idx] };
      this.$set(this.layerData.statuses, idx, { ...prev, parent_id: groupId });
      try {
        const res = await this.api.linkStatusToGroup(statusId, groupId);
        this._assertOk(res, 'link_status_to_group_failed');
        this.$set(this.layerData.statuses, idx, res.status);
        return res.status;
      } catch (e) {
        this.$set(this.layerData.statuses, idx, prev);
        throw e;
      }
    },

    async _addIncident(payload) {
      const tempId = this._tempId();
      if (!Array.isArray(this.layerData.incident_statuses)) {
        this.$set(this.layerData, 'incident_statuses', []);
      }
      const optimistic = {
        id: tempId,
        layer_id: this.layerData.id,
        type: 'incident',
        parent_id: null,
        permissions: [],
        parent_id: payload.parent_id ?? payload.group_id ?? null,
        ...payload,
      };
      this.layerData.incident_statuses.push(optimistic);
      try {
        const res = await this.api.addIncident(payload);
        this._assertOk(res, 'add_incident_failed');
        const i = this.layerData.incident_statuses.findIndex(s => s.id === tempId);
        if (i !== -1) this.$set(this.layerData.incident_statuses, i, res.status);
        return res.status;
      } catch (e) {
        this.layerData.incident_statuses = this.layerData.incident_statuses.filter(s => s.id !== tempId);
        throw e;
      }
    },

    async _updateIncident(id, payload) {
      const idx = (this.layerData.incident_statuses || []).findIndex(s => s.id === id);
      if (idx === -1) throw new Error('incident_not_found');
      const prev = { ...this.layerData.incident_statuses[idx] };
      this.$set(this.layerData.incident_statuses, idx, { ...prev, ...payload });
      try {
        const res = await this.api.updateIncident(id, payload);
        this._assertOk(res, 'update_incident_failed');
        this.$set(this.layerData.incident_statuses, idx, res.status);
        return res.status;
      } catch (e) {
        this.$set(this.layerData.incident_statuses, idx, prev);
        throw e;
      }
    },

    async _deleteIncident(id) {
      const idx = (this.layerData.incident_statuses || []).findIndex(s => s.id === id);
      if (idx === -1) return;
      const prev = this.layerData.incident_statuses[idx];
      this.layerData.incident_statuses.splice(idx, 1);
      try {
        const res = await this.api.deleteIncident(id);
        this._assertOk(res, 'delete_incident_failed', false);
      } catch (e) {
        this.layerData.incident_statuses.splice(idx, 0, prev);
        throw e;
      }
    },

    _assertOk(res, fallbackMsg, requireStatus = true) {
      // Para add/update: requireStatus=true → exige que el backend devuelva el objeto.
      // Para delete: requireStatus=false → solo verifica success !== false.
      logger.debug('assertOk', res, fallbackMsg, requireStatus);
      if (res?.success === false || (requireStatus && !res?.status)) {
        const err = new Error(res?.error_msg || res?.message || fallbackMsg);
        err.error_msg = res?.error_msg || res?.message;
        throw err;
      }
    },
  }
}
</script>
